import { ZfBanner, ZfStack } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBalanceSheet } from '../../api';
import { useYwuApp } from '../../contexts/ywu-app-context';
import { useWealthPlanningNavigate } from '../../hooks/useWealthPlanningNavigate';
import { dismissClmDataUsageBanner, isClmDataUsageBannerDismissed } from '../../utils/clm-data-usage-banner-state';
import { DashboardBannerWrapper } from './DashboardBannerWrapper';

interface IProps {
    onBannerHeightChange: (height: number) => void;
}

export const DashboardBanners: FunctionComponent<IProps> = ({ onBannerHeightChange }) => {
    const { t } = useTranslation();
    const { isWealthNavigatorMFE, isPresentationMode } = useYwuApp();
    const { isOpenedInClientContext } = useWealthPlanningNavigate();
    const { data: balanceSheetResponse } = useBalanceSheet();

    const [isWealthNavigatorBannerVisible, setIsWealthNavigatorBannerVisible] = useState(true);
    const [isMissingDataBannerVisible, setIsMissingDataBannerVisible] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => () => dismissClmDataUsageBanner(), []);

    const handleCopy = async () => {
        if (!balanceSheetResponse) return;
        try {
            await navigator.clipboard.writeText(JSON.stringify(balanceSheetResponse.sources, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const showClmBanner = !isClmDataUsageBannerDismissed() && !isPresentationMode;
    const showWealthNavigatorBanner = isWealthNavigatorMFE && isWealthNavigatorBannerVisible && isOpenedInClientContext && !isPresentationMode;
    const showMissingDataBanner = balanceSheetResponse?.partial && isMissingDataBannerVisible && !isPresentationMode;

    if (!showClmBanner && !showWealthNavigatorBanner && !showMissingDataBanner) {
        return null;
    }

    return (
        <DashboardBannerWrapper onBannerHeightChange={onBannerHeightChange}>
            <ZfStack direction="column" spacing="medium">
                {showClmBanner ? (
                    <ZfBanner type="info" dismissible={false}>
                        {t('clmDataUsageInfoMessage')}
                    </ZfBanner>
                ) : null}
                {showWealthNavigatorBanner ? (
                    <ZfBanner type="info" titleText={t('wealthNavigatorInfoTitle')} onBannerDismiss={() => setIsWealthNavigatorBannerVisible(false)}>
                        {t('wealthNavigatorInfoMessage')}
                    </ZfBanner>
                ) : null}
                {showMissingDataBanner ? (
                    <ZfBanner
                        type="warning"
                        actionButtonText={copied ? t('copied') : t('copyTechnicalInformation')}
                        titleText={t('missingDataInfoTitle')}
                        onBannerActionClick={handleCopy}
                        onBannerDismiss={() => setIsMissingDataBannerVisible(false)}
                    >
                        {t('missingDataInfoMessage')}
                    </ZfBanner>
                ) : null}
            </ZfStack>
        </DashboardBannerWrapper>
    );
};
